import { type ComponentProps, createContext, useContext, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Button } from 'ui/src/components/Button/Button'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { Collapsible } from 'ui/src/components/Collapsible/Collapsible'
import { Switch } from 'ui/src/components/Switch/Switch'
import { Text } from 'ui/src/components/Text/Text'
import { TextField } from 'ui/src/components/TextField/TextField'
import { CrossIconSmall } from 'ui/src/icons/CrossIconSmall'
import { xStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { discountBadge, formInput, formSubmitButton } from './Discount.css'

export const FORM_CAMPAIGN_CODE = 'campaignCode'

type DiscountContext = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}
const Context = createContext<DiscountContext | null>(null)

const useDiscount = () => {
  const context = useContext(Context)

  if (!context) {
    throw new Error('useDiscount must be used inside Discount.Root')
  }

  return context
}

type RootProps = Omit<ComponentProps<typeof Collapsible.Root>, 'open'>
const DiscountRoot = ({ children, defaultOpen, ...props }: RootProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen || false)

  return (
    <Context.Provider value={{ isOpen, setIsOpen }}>
      <Collapsible.Root open={isOpen} {...props}>
        {children}
      </Collapsible.Root>
    </Context.Provider>
  )
}

const DiscountHeader = ({ className, children, ...props }: ComponentProps<'div'>) => {
  return (
    <div className={clsx(xStack({ justifyContent: 'space-between', alignItems: 'center' }), className)} {...props}>
      {children}
    </div>
  )
}

const DiscountToggle = (props: ComponentProps<typeof Switch>) => {
  const { isOpen, setIsOpen } = useDiscount()

  return (
    <Collapsible.Trigger asChild>
      <Switch checked={isOpen} onCheckedChange={setIsOpen} {...props} />
    </Collapsible.Trigger>
  )
}

const DiscountContent = ({ children, ...props }: ComponentProps<typeof Collapsible.Content>) => {
  return (
    <Collapsible.Content {...props}>
      <div className={sprinkles({ paddingTop: 'md' })}>{children}</div>
    </Collapsible.Content>
  )
}

type FormProps = ComponentProps<'form'> & {
  errorMessage?: string
  loading?: boolean
}
const DiscountForm = ({ className, errorMessage, loading, ...props }: FormProps) => {
  const { t } = useTranslation('cart')

  return (
    <form className={clsx(sprinkles({ position: 'relative' }), className)} {...props}>
      <TextField
        className={formInput}
        name={FORM_CAMPAIGN_CODE}
        label={t('CAMPAIGN_CODE_INPUT_LABEL')}
        size="medium"
        warning={!!errorMessage}
        message={errorMessage}
        required={true}
        upperCaseInput={true}
        disabled={loading}
      />

      <Button className={formSubmitButton} type="submit" size="medium" variant="primary" loading={loading}>
        {t('CHECKOUT_ADD_DISCOUNT_BUTTON')}
      </Button>
    </form>
  )
}

type CodeProps = ComponentProps<'form'> & {
  code: string
}
const DiscountCode = ({ children, className, code, ...props }: CodeProps) => {
  return (
    <form
      className={clsx(xStack({ justifyContent: 'space-between', alignItems: 'baseline', gap: 'xs' }), className)}
      {...props}
    >
      <Badge className={discountBadge} variant="grey1" size="small">
        <Text as="span" size="xs">
          {code}
        </Text>
        <input hidden name={FORM_CAMPAIGN_CODE} value={code} readOnly />
        <IconButton variant="ghost" style={{ padding: 0 }} data-no-hover>
          <CrossIconSmall color={tokens.legacyColors.fillOpaqueSecondary} />
        </IconButton>
      </Badge>
      {children && (
        <Text align="right" balance={true}>
          {children}
        </Text>
      )}
    </form>
  )
}

export const Discount = {
  Root: DiscountRoot,
  Header: DiscountHeader,
  Toggle: DiscountToggle,
  Content: DiscountContent,
  Form: DiscountForm,
  Code: DiscountCode,
}
